import graphql from "babel-plugin-relay/macro";
import { readInlineData } from "react-relay";

import { getFacilityMemberPermissions_facility$key } from "./__generated__/getFacilityMemberPermissions_facility.graphql";

export type getFacilityMemberPermissionsProps = {
  fragmentRef: getFacilityMemberPermissions_facility$key | null;
};

export type MemberAccess =
  | "AVAILABILITY_ONLY"
  | "HIDDEN"
  | "VIEW_ONLY"
  | "VISIBLE"
  | "JOINABLE"
  | "FACILITY_PERMISSIONS_DISABLED";

export const getFacilityMemberPermissions = ({
  fragmentRef,
}: getFacilityMemberPermissionsProps) => {
  const facilityPermissionSet = readInlineData(
    getFacilityMemberPermissionsFragmentString,
    fragmentRef
  );

  const getApplicablePermissionSet = () => {
    if (facilityPermissionSet) {
      const { club, publicBookingPermissionSet, privateBookingPermissionSet } =
        facilityPermissionSet;
      const { supportsPublic, supportsPrivate } = club || {};

      if (supportsPublic && publicBookingPermissionSet) {
        return publicBookingPermissionSet;
      }

      if (supportsPrivate && privateBookingPermissionSet) {
        return privateBookingPermissionSet;
      }
    }

    return {
      isFacilityVisible: false,
      isFacilityBookable: false,
      areOtherBookingsVisible: false,
      areOtherBookingsJoinable: false,
      canBookOnBusySlot: false,
    };
  };

  const permissionSet = getApplicablePermissionSet();

  const AVAILABILITY_ONLY = permissionSet?.isFacilityVisible;
  const HIDDEN = AVAILABILITY_ONLY && permissionSet?.isFacilityBookable;
  const VIEW_ONLY = AVAILABILITY_ONLY && permissionSet?.areOtherBookingsVisible;
  const VISIBLE = VIEW_ONLY && permissionSet?.isFacilityBookable;
  const JOINABLE = VISIBLE && permissionSet?.areOtherBookingsJoinable;

  // Return hierarchy of permissions based on how restrictive they are
  const memberAccess: MemberAccess = JOINABLE
    ? "JOINABLE"
    : VISIBLE
    ? "VISIBLE"
    : VIEW_ONLY
    ? "VIEW_ONLY"
    : HIDDEN
    ? "HIDDEN"
    : AVAILABILITY_ONLY
    ? "AVAILABILITY_ONLY"
    : "FACILITY_PERMISSIONS_DISABLED";

  return {
    memberAccess: memberAccess,
    canBookOnBusySlot: permissionSet?.canBookOnBusySlot,
  };
};

export type FacilityMemberPermissions = ReturnType<
  typeof getFacilityMemberPermissions
>;

export const getFacilityMemberPermissionsFragmentString = graphql`
  fragment getFacilityMemberPermissions_facility on Facility @inline {
    club {
      id
      supportsPrivate
      supportsPublic
    }
    publicBookingPermissionSet {
      id
      isFacilityVisible
      isFacilityBookable
      areOtherBookingsVisible
      areOtherBookingsJoinable
      canBookOnBusySlot
    }
    privateBookingPermissionSet {
      id
      isFacilityVisible
      isFacilityBookable
      areOtherBookingsVisible
      areOtherBookingsJoinable
      canBookOnBusySlot
    }
  }
`;
